// BOM Table — Step 5 Results.
// Displays BOM line items grouped by category with expandable methodology notes.

import { useState } from 'react';
import type { BOMLineItem } from '../../types';

type CategoryFilter = 'All' | BOMLineItem['category'];

const CATEGORY_COLOURS: Record<BOMLineItem['category'], string> = {
  Compute: '#1e40af',
  Storage: '#065f46',
  Network: '#7c2d12',
  Licensing: '#4c1d95',
  Security: '#92400e',
  'Load Balancing': '#0f172a',
};

const SITE_BADGE: Record<BOMLineItem['site'], { bg: string; text: string }> = {
  Primary: { bg: '#dbeafe', text: '#1e40af' },
  DR:      { bg: '#fce7f3', text: '#9d174d' },
  Both:    { bg: '#f3f4f6', text: '#374151' },
};

interface Props {
  bom: BOMLineItem[];
}

export function BOMTable({ bom }: Props) {
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('All');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const categories: CategoryFilter[] = [
    'All',
    ...Array.from(new Set(bom.map(item => item.category))),
  ];

  const filtered = activeCategory === 'All'
    ? bom
    : bom.filter(item => item.category === activeCategory);

  const toggleRow = (i: number) =>
    setExpandedRow(prev => (prev === i ? null : i));

  return (
    <div>
      {/* Category filter pills */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            style={{
              padding: '4px 14px',
              borderRadius: 9999,
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              background: activeCategory === cat ? '#1e40af' : '#f1f5f9',
              color: activeCategory === cat ? '#fff' : '#334155',
              transition: 'background 0.15s',
            }}
          >
            {cat}
            {cat !== 'All' && (
              <span style={{ marginLeft: 6, opacity: 0.7 }}>
                ({bom.filter(i => i.category === cat).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p style={{ color: '#64748b', fontStyle: 'italic' }}>No BOM items in this category.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Site</th>
                <th style={thStyle}>Tier</th>
                <th style={thStyle}>Env</th>
                <th style={{ ...thStyle, width: '30%' }}>Description</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Qty</th>
                <th style={thStyle}>Unit</th>
                <th style={{ ...thStyle, width: '25%' }}>Spec Notes</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, i) => {
                const catColour = CATEGORY_COLOURS[item.category] ?? '#374151';
                const siteBadge = SITE_BADGE[item.site];
                const isExpanded = expandedRow === i;

                return (
                  <>
                    <tr
                      key={i}
                      style={{
                        borderBottom: isExpanded ? 'none' : '1px solid #e2e8f0',
                        background: isExpanded ? '#f8fafc' : 'transparent',
                      }}
                    >
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          background: catColour + '1a',
                          color: catColour,
                          letterSpacing: '0.03em',
                        }}>
                          {item.category}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          background: siteBadge.bg,
                          color: siteBadge.text,
                        }}>
                          {item.site}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, color: '#64748b', fontSize: 12 }}>
                        {item.tierName ?? '—'}
                      </td>
                      <td style={{ ...tdStyle, color: '#64748b', fontSize: 12 }}>
                        {item.environment === 'all' ? '—' : item.environment.replace('_', ' ')}
                      </td>
                      <td style={tdStyle}>{item.description}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {item.quantity.toLocaleString()}
                      </td>
                      <td style={{ ...tdStyle, color: '#64748b' }}>{item.unit}</td>
                      <td style={{ ...tdStyle, color: '#475569', fontSize: 12 }}>{item.specNotes}</td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => toggleRow(i)}
                          title="Show methodology note"
                          style={{
                            padding: '2px 8px',
                            borderRadius: 4,
                            border: '1px solid #e2e8f0',
                            background: isExpanded ? '#e2e8f0' : 'transparent',
                            cursor: 'pointer',
                            fontSize: 11,
                            color: '#64748b',
                          }}
                        >
                          {isExpanded ? '▲ hide' : '▼ why'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${i}-expanded`} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td colSpan={9} style={{
                          padding: '8px 16px 12px 16px',
                          background: '#f8fafc',
                          borderLeft: '3px solid #1e40af',
                        }}>
                          <p style={{ margin: 0, fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                            <strong style={{ color: '#1e40af' }}>Methodology: </strong>
                            {item.methodologyNote}
                          </p>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ marginTop: 12, fontSize: 12, color: '#94a3b8' }}>
        {filtered.length} line item{filtered.length !== 1 ? 's' : ''} shown.
        Click <em>▼ why</em> to view the methodology note for any line.
      </p>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 12,
  color: '#475569',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  verticalAlign: 'top',
};
